import { useGameStore } from '~/stores/games.store';
import type { FetchContext, FetchResponse } from 'ofetch';
import type {
  IFetchGameRequest,
  IGameCategoriesRequest,
  IGameProvidersRequest,
  IGameRatingRequest,
  IGetGameRating,
  IPlaytimeRequest,
  ISearchRequest,
} from '~/interfaces/dto/games/request';
import type {
  IFavouriteResponse,
  IGameCategories,
  IGameCategoriesResponse,
  IGameCategoriesV4,
  IGameCategoriesV4Data,
  IGameCategoryResponse,
  ISearchResponse,
  TRatingMapping,
} from '~/interfaces/dto/games/response';

export const useGameService = () => {
  const { $fetch, $dayjs } = useNuxtApp();
  const personalised = usePersonalised();

  //stores
  const gameStore = useGameStore();
  const fetchBuildVersion = async () => {
    return await $fetch(`api/v3/Gaming/BuildVersion`, {
      base: 'baseC',
      useRegionCode: true,
      useEnvironment: true,
      async onResponse(context) {
        gameStore.setBuildInformation(context.response._data);
      },
    }).catch((e) => console.error(e));
  };
  const fetchGamesByProvider = async ({
    count = 50,
    provider,
    skip = 0,
    limit = 50,
  }: IGameProvidersRequest) => {
    const params = {
      count,
      provider,
      skip,
      limit,
    };

    return await $fetch<IGameCategoriesResponse>(
      `api/v3/Gaming/Provider/Games`,
      {
        base: 'baseC',
        useRegionCode: true,
        useVertical: true,
        useEnvironment: true,
        useChannel: true,
        useLanguageCode: true,
        useCurrency: true,
        query: params,
        async onResponse(context) {
          const transformedData = context.response._data?.data?.map((game) => {
            return { ...game, provider };
          });
          context.response._data = {
            ...context.response._data,
            data: transformedData,
          };
        },
      },
    );
  };
  const fetchCategory = async ({
    count = 50,
    vertical,
    category,
    skip = 0,
    limit,
  }: IGameCategoriesRequest) => {
    const params = {
      count,
      category,
      skip,
      limit,
      vertical,
    };
    return await $fetch<IGameCategoriesResponse>(
      `api/v3/Gaming/{channel}/${category}`,
      {
        base: 'baseC',
        useChannel: true,
        useLanguageCode: true,
        useCurrency: true,
        useRegionCode: true,
        useVertical: !vertical,
        useEnvironment: true,
        query: params,
        async onResponse(context) {
          const transformedData = context.response._data?.data?.map((game) => {
            return { ...game, category };
          });
          context.response._data = {
            ...context.response._data,
            data: transformedData,
          };
        },
      },
    );
  };
  const fetchMostLikedGames = async (
    { count, gameCount }: IGameCategoriesRequest = {
      count: 10,
      gameCount: 10,
    },
  ) => {
    const params = {
      limit: gameCount || count,
    };

    return await $fetch<IGameCategories[]>('api/v3/Gaming/Game/MostLiked', {
      base: 'baseC',
      useVertical: true,
      useLanguageCode: true,
      useChannel: true,
      useCurrency: true,
      useRegionCode: true,
      useEnvironment: true,
      query: params,
      onResponse(context) {
        if (!!context.response._data?.length) {
          personalised.setPersonalisedData('mostLiked', context.response._data);
        }
      },
      onRequestError() {
        personalised.setPersonalisedData('mostLiked', []);
      },
    });
  };
  const fetchRecommendedGames = async (
    { count, gameCount }: IGameCategoriesRequest = {
      count: 10,
      gameCount: 10,
    },
  ) => {
    const params = {
      includeLikedGames: true,
      limit: gameCount || count,
    };

    return await $fetch<IGameCategories[]>('api/v3/Gaming/Game/Recommended', {
      base: 'base',
      useVertical: true,
      useLanguageCode: true,
      useChannel: true,
      useCurrency: true,
      useRegionCode: true,
      useAuthentication: true,
      useEnvironment: true,
      query: params,
      onResponse(context) {
        if (!!context.response._data?.length) {
          personalised.setPersonalisedData(
            'recommended',
            context.response._data,
          );
        }
      },
      onRequestError() {
        personalised.setPersonalisedData('recommended', []);
      },
    });
  };
  const fetchPersonalisedGames = async (
    { count, gameCount }: IGameCategoriesRequest = {
      count: 10,
      gameCount: 10,
    },
  ) => {
    const params = {
      includeRecentGames: true,
      includeLikedGames: true,
      limit: gameCount || count,
    };

    return await $fetch<{
      favourites: IGameCategoriesV4;
      recommended: IGameCategoriesV4;
      recents: IGameCategoriesV4;
      mostLiked: IGameCategoriesV4;
    }>('api/v3/Gaming/Game/Personalised', {
      base: 'base',
      useVertical: true,
      useLanguageCode: true,
      useChannel: true,
      useCurrency: true,
      useRegionCode: true,
      useAuthentication: true,
      useEnvironment: true,
      cacheBustKey: 'personalised',
      query: params,
      async onResponse(context) {
        const { favourites, recommended, recents, mostLiked } =
          context.response._data;
        const transformedData = (favourites || [])?.map((game) => {
          return { ...game, categories: { favourites: 1, ...game.categories } };
        });
        personalised.setPersonalisedData('favorites', transformedData);
        personalised.setPersonalisedData('recentlyPlayed', recents);
        personalised.setPersonalisedData('mostLiked', mostLiked);
        personalised.setPersonalisedData('recommended', recommended);
        gameStore.setRecentSearches(
          personalised.getPersonalisedData(
            'recentSearches',
            'Local',
          ) as string[],
        );
      },
    });
  };
  //v4 api
  const fetchCategories = async ({
    count,
    gameCount,
    freePlayOnly,
  }: IGameCategoriesRequest) => {
    const params = {
      count,
      gameCount,
      freePlayOnly,
    };

    return await $fetch<IGameCategoriesV4Data>(
      'api/v4/Gaming/Game/Categories',
      {
        base: 'baseC',
        useChannel: true,
        useLanguageCode: true,
        useCurrency: true,
        useEnvironment: true,
        useVertical: true,
        useRegionCode: true,
        query: params,
        useCache: true,
        async onResponse(
          context: FetchContext & { response: FetchResponse<ResponseType> },
        ): Promise<void> {
          const data = context.response._data;
          const isLaunchGame =
            data?.categories?.length === 1 &&
            data.categories[0]?.games?.length === 1;
          const gameToLaunch = isLaunchGame
            ? {
                gameToLaunch: data.categories[0]?.games[0],
              }
            : {};
          const isSingleCategory = data.categories?.length === 1;
          const category = isSingleCategory
            ? { category: data.categories[0]?.name }
            : {};
          const hasFreePlay =
            data.categories?.some((categoryAndGames) =>
              categoryAndGames.games?.some(
                (game) => game.freeplay && !game.unavailable,
              ),
            ) || false;
          context.response._data = {
            ...data,
            isLaunchGame,
            isSingleCategory,
            ...gameToLaunch,
            ...category,
            hasFreePlay,
          };
        },
        onResponseError(context: FetchContext) {
          context.response._data = {
            categories: [],
            themes: [],
            providers: [],
            isSingleCategory: false,
          };
        },
      },
    );
  };
  const fetchGame = async ({ gameId }: IFetchGameRequest) => {
    return await $fetch<IGameCategoryResponse>(`api/v1/Gaming/Game/${gameId}`, {
      base: 'baseC',
      useChannel: true,
      useLanguageCode: true,
      useCurrency: true,
      useEnvironment: true,
      useRegionCode: true,
      useVertical: true,
      async onResponse(
        context: FetchContext & {
          response: FetchResponse<IGameCategoriesV4>;
        },
      ): Promise<void> {
        gameStore.setCurrentGame(context.response._data);
      },
    });
  };
  const fetchGameByAlias = async ({ alias }: IFetchGameRequest) => {
    return await $fetch(`api/v1/Gaming/Game/${alias}/alias`, {
      base: 'baseC',
      useChannel: true,
      useLanguageCode: true,
      useCurrency: true,
      useEnvironment: true,
      useVertical: true,
      useRegionCode: true,
      onResponse(
        context: FetchContext & {
          response: FetchResponse<IGameCategoriesV4>;
        },
      ): void {
        gameStore.setCurrentGame(context.response._data);
      },
    });
  };
  //v2 global search
  const fetchSearchedGames = async ({
    search,
    skip,
    limit,
  }: ISearchRequest) => {
    const params = {
      search,
      skip,
      limit,
    };
    return await $fetch<ISearchResponse>('api/v4/Gaming/Search', {
      query: params,
      base: 'baseC',
      useRegionCode: true,
      useChannel: true,
      useEnvironment: true,
      useCurrency: true,
      useLanguageCode: true,
      onResponse({ response }) {
        const verticals = response._data.verticals.filter((vertical) => {
          return vertical.vertical !== 'Certification';
        });
        Object.assign(response._data, { verticals });

        return response._data;
      },
    });
  };
  const addGameFavourites = async ({
    game,
  }: {
    game: IGameCategory;
  }): Promise<IFavouriteResponse> => {
    if (!gameStore.favoriteIds.includes(String(game.id))) {
      return await $fetch<IFavouriteResponse>(
        `api/v1/Gaming/Game/${game.id}/Favourite`,
        {
          base: 'base',
          useAuthentication: true,
          useChannel: true,
          useCurrency: true,
          useLanguageCode: true,
          useEnvironment: true,
          useVertical: true,
          method: 'post',
          body: {},
          cacheBustKey: 'personalised',
          onResponse({ response }) {
            personalised.setPersonalisedData('favorites', response._data);
          },
        },
      );
    }
    return;
  };
  const deleteGameFavourites = async ({
    game,
  }: {
    game: IGameCategory;
  }): Promise<IFavouriteResponse> => {
    return await $fetch<IFavouriteResponse>(
      `api/v1/Gaming/Game/${game.id}/Favourite`,
      {
        base: 'base',
        useAuthentication: true,
        useChannel: true,
        useCurrency: true,
        useLanguageCode: true,
        useEnvironment: true,
        useVertical: true,
        method: 'delete',
        body: {},
        cacheBustKey: 'personalised',
        onResponse({ response }) {
          personalised.setPersonalisedData('favorites', response._data);
        },
      },
    );
  };
  const rateGame = async ({ gameId, rating }: IGameRatingRequest) => {
    const params = {
      gameId,
      rating,
    };

    return await $fetch<IFavouriteResponse>(
      `api/v1/Gaming/Game/${gameId}/rating/${rating}`,
      {
        base: 'base',
        useAuthentication: true,
        useEnvironment: true,
        method: 'post',
        body: {},
        query: params,
      },
    );
  };
  const getRating = async ({ gameId }: IGetGameRating) => {
    return await $fetch<{ casinoGameRating: TRatingMapping }>(
      `api/v1/Gaming/${gameId}/rating`,
      {
        base: 'base',
        useAuthentication: true,
        useEnvironment: true,
      },
    );
  };
  const logPlaytime = async ({ gameId, startDateTime }: IPlaytimeRequest) => {
    return await $fetch<IFavouriteResponse>(
      `api/v1/Gaming/Game/${gameId}/playtime`,
      {
        method: 'post',
        base: 'base',
        useEnvironment: true,
        useAuthentication: true,
        body: { startDateTime, endDateTime: $dayjs().toISOString() },
      },
    );
  };

  return {
    fetchGamesByProvider,
    fetchCategories,
    fetchGame,
    fetchGameByAlias,
    fetchCategory,
    fetchSearchedGames,
    fetchRecommendedGames,
    fetchMostLikedGames,
    fetchPersonalisedGames,
    addGameFavourites,
    deleteGameFavourites,
    rateGame,
    getRating,
    fetchBuildVersion,
    logPlaytime,
  };
};
